import type {
  AgentEdge,
  AgentNode,
  AgentNodeData,
} from '@saaga/shared/interfaces/agency-node.interface'
import type { Edge, Node, NodeTypes } from '@xyflow/react'
import { Background, Controls, ReactFlow, ReactFlowProvider } from '@xyflow/react'
import { type ReactNode, useMemo } from 'react'

import { cn } from '@/lib/utils'

import '@xyflow/react/dist/style.css'
import './agency-flow-canvas.css'

import {
  BACKGROUND_CONFIG,
  CANVAS_BACKGROUND_STYLE,
  REACT_FLOW_CONFIG,
  REACT_FLOW_FIT_VIEW_OPTIONS,
} from './agency-flow-canvas.config'
import { getLayoutedElements } from './agency-flow-layout.util'
import { AgentNodePreview } from './agent-node-preview.atom'

interface AgencyFlowPreviewProps {
  nodeList: AgentNode[]
  edgeList: AgentEdge[]
  className?: string
}

const AgencyFlowPreviewInner = ({
  nodeList,
  edgeList,
  className,
}: AgencyFlowPreviewProps): ReactNode => {
  const nodeTypes: NodeTypes = useMemo(
    () => ({
      agent: (props: { data: AgentNodeData }) => <AgentNodePreview data={props.data} />,
    }),
    []
  )

  const { nodeList: layoutedNodeList, edgeList: layoutedEdgeList } = useMemo(
    () => getLayoutedElements(nodeList, edgeList, 'TB'),
    [nodeList, edgeList]
  )

  return (
    <div
      className={cn('agency-flow-preview h-full w-full', className)}
      style={CANVAS_BACKGROUND_STYLE}
    >
      <ReactFlow
        nodes={layoutedNodeList as Node[]}
        edges={layoutedEdgeList as Edge[]}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        connectionLineType={REACT_FLOW_CONFIG.connectionLineType}
        fitView
        fitViewOptions={REACT_FLOW_FIT_VIEW_OPTIONS}
        minZoom={REACT_FLOW_CONFIG.minZoom}
        maxZoom={REACT_FLOW_CONFIG.maxZoom}
        defaultEdgeOptions={REACT_FLOW_CONFIG.defaultEdgeOptions}
        proOptions={{ hideAttribution: true }}
      >
        <Background {...BACKGROUND_CONFIG} />
        <Controls position="bottom-left" showInteractive={false} />
      </ReactFlow>
    </div>
  )
}

export const AgencyFlowPreview = (props: AgencyFlowPreviewProps): ReactNode => (
  <ReactFlowProvider>
    <AgencyFlowPreviewInner {...props} />
  </ReactFlowProvider>
)

AgencyFlowPreview.displayName = 'AgencyFlowPreview'
